import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { SelectInput } from '@themes/aegix';
import { useAllPermissions } from '../../hooks/queries/usePermissions';
import {
  PermissionCategoryMeta,
  PermissionLevelOrCustom,
} from '../../types/role';
import {
  BAND_ORDER,
  LEVEL_LABELS,
  RolePermissionSelection,
  bandLabel,
  buildPermissionCatalog,
  getLevelLabel,
  inferRoleSelection,
  selectedCountForCategory,
  selectionToPermissionIds,
} from '../../utils/permission-levels';

interface RolePermissionPickerProps {
  selectedPermissions?: number[];
  onChange?: (ids: number[]) => void;
  disabled?: boolean;
}

const signatureOf = (ids: number[]): string =>
  [...ids].sort((a, b) => a - b).join(',');

/**
 * ADR-048 tiered role permission picker (D8).
 *
 * Renders the catalog from `GET /api/v2/permissions/` as three audience bands,
 * each with its categories; every category gets a level control showing only
 * its applicable levels (Decision 4), and a `Capabilities` section lists the
 * cross-cutting toggles. A role whose set falls between tiers shows `Custom`
 * with its raw codenames. Load infers the per-category level; save expands
 * (category, level) + capabilities back to ids while preserving the role's
 * leftover (non-catalog) permissions and any `custom` raw set — permissions are
 * never silently dropped (Decision 7).
 *
 * The component works in permission *ids* at its boundary (the role's stored
 * representation and what the roles endpoint accepts), bridging to the
 * codename-keyed catalog internally.
 */
export default function RolePermissionPicker({
  selectedPermissions = [],
  onChange,
  disabled = false,
}: RolePermissionPickerProps) {
  const { t } = useTranslation();
  // Permissions fetch goes through React Query (ADR 102) so it participates
  // in the invalidate-on-org-switch contract.
  const {
    data: permissionsResponse,
    isLoading: loading,
    error: queryError,
  } = useAllPermissions();

  const catalog = useMemo(() => {
    if (!permissionsResponse) return null;
    return buildPermissionCatalog(
      permissionsResponse.results ?? [],
      permissionsResponse.categories ?? [],
      permissionsResponse.capabilities ?? [],
    );
  }, [permissionsResponse]);

  const [selection, setSelection] = useState<RolePermissionSelection | null>(
    null,
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const lastEmittedSig = useRef<string | null>(null);

  const incomingSig = useMemo(
    () => signatureOf(selectedPermissions),
    [selectedPermissions],
  );

  useEffect(() => {
    if (!catalog) return;
    if (selection !== null && lastEmittedSig.current === incomingSig) return;
    setSelection(inferRoleSelection(selectedPermissions, catalog));
    lastEmittedSig.current = incomingSig;
  }, [catalog, incomingSig, selectedPermissions, selection]);

  const emit = useCallback(
    (next: RolePermissionSelection) => {
      if (!catalog) return;
      setSelection(next);
      const ids = selectionToPermissionIds(next, catalog);
      lastEmittedSig.current = signatureOf(ids);
      onChange?.(ids);
    },
    [catalog, onChange],
  );

  const setCategoryLevel = useCallback(
    (key: string, value: PermissionLevelOrCustom) => {
      if (disabled || !selection) return;
      emit({
        ...selection,
        levelByCategory: { ...selection.levelByCategory, [key]: value },
      });
    },
    [disabled, selection, emit],
  );

  const toggleCapability = useCallback(
    (id: number) => {
      if (disabled || !selection) return;
      const has = selection.capabilityIds.includes(id);
      emit({
        ...selection,
        capabilityIds: has
          ? selection.capabilityIds.filter((x) => x !== id)
          : [...selection.capabilityIds, id],
      });
    },
    [disabled, selection, emit],
  );

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const bands = useMemo(() => {
    if (!catalog) return [];
    const byBand = new Map<string, PermissionCategoryMeta[]>();
    for (const category of catalog.categories) {
      const list = byBand.get(category.band) ?? [];
      list.push(category);
      byBand.set(category.band, list);
    }
    const ordered: { band: string; categories: PermissionCategoryMeta[] }[] =
      [];
    for (const band of BAND_ORDER) {
      const list = byBand.get(band);
      if (list?.length) ordered.push({ band, categories: list });
    }
    for (const [band, list] of byBand) {
      if (!(BAND_ORDER as readonly string[]).includes(band) && list.length) {
        ordered.push({ band, categories: list });
      }
    }
    return ordered;
  }, [catalog]);

  const capabilities = useMemo(() => {
    if (!catalog) return [];
    return catalog.capabilities
      .map((cap) => ({ ...cap, id: catalog.idByCodename.get(cap.codename) }))
      .filter(
        (cap): cap is typeof cap & { id: number } => cap.id !== undefined,
      );
  }, [catalog]);

  if (loading || (!catalog && !queryError)) {
    return (
      <Center py={8}>
        <Spinner size="lg" />
      </Center>
    );
  }

  if (queryError || !catalog || !selection) {
    return (
      <Center py={8}>
        <Text color="text.danger">
          {t('forms.failedLoadPermissions', 'Failed to load permissions')}
        </Text>
      </Center>
    );
  }

  const totalSelected = selectionToPermissionIds(selection, catalog).length;

  return (
    <VStack gap={4} align="stretch" data-e2e="role-permission-picker">
      <Text fontSize="xs" color="fg.muted">
        {t(
          'forms.permissionsSelectedCount',
          '{{count}} permission{{plural}} selected',
          {
            count: totalSelected,
            plural: totalSelected !== 1 ? 's' : '',
          },
        )}
      </Text>

      {bands.map(({ band, categories }) => (
        <VStack key={band} gap={2} align="stretch">
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            color="fg.muted"
          >
            {bandLabel(band, t)}
          </Text>

          {categories.map((category) => {
            const value = selection.levelByCategory[category.key] ?? 'none';
            const applicable = catalog.applicableLevels.get(category.key) ?? [];
            const options: { label: string; value: PermissionLevelOrCustom }[] =
              applicable.map((level) => ({
                label: getLevelLabel(level, t),
                value: level,
              }));
            // Keep `Custom` available whenever the role was originally between
            // tiers, so the user can both leave it and return to the preserved
            // raw set rather than being forced to discard it.
            if (selection.customByCategory[category.key]) {
              options.push({
                label: getLevelLabel('custom', t),
                value: 'custom',
              });
            }
            const count = selectedCountForCategory(
              selection,
              catalog,
              category.key,
            );
            const isExpanded = expanded.has(category.key);
            const customCodenames =
              selection.customCodenamesByCategory[category.key] ?? [];

            return (
              <Box
                key={category.key}
                borderWidth="1px"
                borderColor="border.muted"
                borderRadius="md"
                p={3}
              >
                <HStack justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    {t(
                      `permissions.categories.${category.key}`,
                      category.label,
                    )}
                  </Text>
                  <Badge
                    size="sm"
                    variant="subtle"
                    colorPalette={count > 0 ? 'blue' : 'gray'}
                    flexShrink={0}
                  >
                    {count}{' '}
                    {count === 1
                      ? t('permissions.permission', 'permission')
                      : t('permissions.permissions', 'permissions')}
                  </Badge>
                </HStack>

                <SelectInput
                  name={`role-permission-level-${category.key}`}
                  value={value}
                  option={options}
                  searchable={false}
                  disabled={disabled}
                  onChange={(next) =>
                    setCategoryLevel(
                      category.key,
                      next as PermissionLevelOrCustom,
                    )
                  }
                  e2eTitle={`role-permission-level-${category.key}`}
                />

                {value === 'custom' && (
                  <VStack gap={1} align="stretch" mt={2}>
                    <Button
                      size="xs"
                      variant="ghost"
                      alignSelf="flex-start"
                      onClick={() => toggleExpanded(category.key)}
                      data-e2e={`role-permission-custom-toggle-${category.key}`}
                    >
                      <HStack gap={1}>
                        {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
                        <Text fontSize="xs">
                          {isExpanded
                            ? t('common.hide', 'Hide')
                            : t('common.show', 'Show')}{' '}
                          {t(
                            'permissions.customPermissionsCount',
                            '{{count}} custom permission{{plural}}',
                            {
                              count: customCodenames.length,
                              plural: customCodenames.length !== 1 ? 's' : '',
                            },
                          )}
                        </Text>
                      </HStack>
                    </Button>
                    {isExpanded && (
                      <Box
                        bg="bg.muted"
                        borderRadius="md"
                        p={2}
                        maxHeight="40"
                        overflowY="auto"
                      >
                        <VStack gap={0.5} align="stretch">
                          {customCodenames.map((codename) => (
                            <Text
                              key={codename}
                              fontSize="xs"
                              fontFamily="mono"
                              color="fg.muted"
                            >
                              {codename}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                )}
              </Box>
            );
          })}
        </VStack>
      ))}

      {capabilities.length > 0 && (
        <VStack gap={2} align="stretch">
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            color="fg.muted"
          >
            {t('permissions.capabilities', 'Capabilities')}
          </Text>
          <VStack gap={1.5} align="stretch">
            {capabilities.map((capability) => (
              <Checkbox.Root
                key={capability.codename}
                checked={selection.capabilityIds.includes(capability.id)}
                onCheckedChange={() => toggleCapability(capability.id)}
                disabled={disabled}
                cursor={disabled ? 'default' : 'pointer'}
                borderWidth="1px"
                borderColor="border.muted"
                borderRadius="md"
                p={3}
                alignItems="center"
                width="100%"
                data-e2e={`role-capability-${capability.codename}`}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control borderRadius="md" />
                <Checkbox.Label flex={1}>
                  <Text fontSize="sm">
                    {t(
                      `permissions.capabilities.${capability.codename}`,
                      capability.label,
                    )}
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </VStack>
        </VStack>
      )}
    </VStack>
  );
}
